import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";

interface Props {
  label: string;
  value: string;
  placeholder?: string;
  rows?: number;
  hint?: string;
  /** Shows a `used/max` counter and turns red past the limit. */
  maxLength?: number;
  error?: string | null;
  onChange: (next: string) => void;
}

/**
 * Multi-line sibling of {@link TextField}. Added for KLUB-119 (#324): the
 * coming-soon notice a member reads is a sentence, not a label, and a 36px
 * single-line input hides most of it while it is being written.
 *
 * Deliberately NOT `maxLength` on the element — a hard cut swallows the last
 * keystrokes with no explanation. The counter goes red instead, and the save
 * button is what refuses.
 */
export function TextAreaField({
  label,
  value,
  placeholder,
  rows = 3,
  hint,
  maxLength,
  error,
  onChange,
}: Props) {
  const over = maxLength !== undefined && value.length > maxLength;
  const invalid = over || !!error;

  return (
    <label style={{ display: "block", marginBottom: space.md }}>
      <span style={{
        display: "block", fontSize: fontSize.sm, fontWeight: fontWeight.medium,
        color: colors.stone, marginBottom: 6,
      }}>
        {label}
      </span>
      <textarea
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%", padding: `9px ${space.md}px`,
          background: colors.white,
          border: `1px solid ${invalid ? colors.error : colors.fog}`,
          borderRadius: radius.sm, fontSize: fontSize.md, color: colors.ink,
          fontFamily: "inherit", lineHeight: 1.45, outline: "none", resize: "vertical",
        }}
      />
      {(hint || maxLength !== undefined || error) && (
        <span style={{
          display: "flex", justifyContent: "space-between", gap: space.sm,
          fontSize: fontSize.xs, marginTop: 4,
          color: invalid ? colors.error : colors.stoneSoft,
        }}>
          <span>{error ?? hint ?? ""}</span>
          {maxLength !== undefined && <span>{value.length}/{maxLength}</span>}
        </span>
      )}
    </label>
  );
}
